import {Component, OnDestroy, OnInit, signal, ViewChild, ViewEncapsulation} from '@angular/core';
import {SnapshotAction} from '@angular/fire/compat/database/interfaces';
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute, NavigationExtras, Router} from '@angular/router';
import {Actions} from '@ngneat/effects-ng';
/** Models */
import {TranslateService} from '@ngx-translate/core';
import {
  constants,
  DialogWidth,
  formatSfTypes,
  IntercomEventsByArea,
  IntercomEventService,
  IntercomProductAreas,
  LicenseService,
  OrgInfo,
  PermissionsService,
  Q9ElementsGptService,
  Q9HtmlDecoderPipe,
  REF_MODEL_STORE,
  Refmodel,
  RefModelNode,
  RefModelStoreService,
  REPORT_MODAL_TYPE,
  SalesForceData,
  SentryService,
  SnackBarService,
  TakeUntilComponentDestroy,
  User,
  UserTeam
} from '@q9elements/ui-kit/common';
import {ElementsPricingCalculatorComponent, loadEntities} from '@q9elements/ui-kit/components';
import {FirebaseAbstractService} from '@q9elements/ui-kit/refmodels';
import {endsWith, get} from 'lodash';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {filter, map, tap} from 'rxjs/operators';
import {ICustomView} from 'src/app/editor-interface/models/custom-view.interface';
import {isTreeListView} from 'src/app/util/metadata-view-typeguard';

import {environment} from '../../environments/environment';
import {ChangeMonitoringPaywallComponent} from '../access-change-monitoring/components/change-monitoring-paywall/change-monitoring-paywall.component';
import {RunToolbarCodeSearchDialogComponent} from '../code-search/components/run-toolbar-code-search-dialog/run-toolbar-code-search-dialog.component';
import {RmeNodeDaoAbstractService} from '../core/abstract/rme-node-dao-abstract.service';
import {AuthService} from '../core/services/auth.service';
import {CustomViewDaoService} from '../core/services/custom-view-dao.service';
import {RmeNodeDaoService} from '../core/services/rme-node-dao.service';
import {GridSelectorService} from '../custom-grid/services/grid-selector.service';
import {BDReportParams} from '../models/rme/big-data-reports.interface';
import {ToolbarConfig} from '../models/rme/toolbar-config.interface';
import {TreeNode} from '../models/rme/tree-node';
import {ContextMenuActionsService} from '../new-context-menu/services/context-menu-actions.service';
import {AttachmentsService} from '../services/dao/attachments.service';
import {RefModelService} from '../services/ref-model.service';
import {SfApiService} from '../services/sf-api.service';
import {UpdateTreeService} from '../services/update-tree.service';
import {ReportDialogService} from '../shared/services/report-dialog.service';
import {ShareRefModelDialogService} from '../shared/services/share-refModel-dialog.service';
import {AiChatDialogComponent} from './components/ai-chat-dialog/ai-chat-dialog.component';
import {DeleteNodeBaseDialogService} from './components/extended/components/delete-node-base-dialog/delete-node-base-dialog.service';
import {DeleteNodeDialogComponent} from './components/extended/delete-node-dialog/delete-node-dialog.component';
import {NodeTreeGridComponent} from './components/node-tree-grid/node-tree-grid.component';
import {DeleteReferenceModelNodesStatusEnum} from './models/enums/ReferenceModel.enum';
import {TreeListViewEnum} from './models/enums/TreeListView.enum';
import {ITreeNode} from './models/grid-node.interface';
import {SearchNodeConfig} from './models/search-node-config.interface';
import {CustomViewService} from './services/custom-view.service';
import {ElementsGptService, ExternalGptConfig} from './services/elements-gpt.service';
import {NodeRemoveEmitterService} from './services/node-remove-emitter.service';
import {RequirementAttachmentsService} from './services/requirement-attachments.service';
import {TreePaginationService} from './services/tree-pagination.service';

@Component({
  selector: 'app-editor-interface',
  templateUrl: './editor-interface.component.html',
  styleUrls: ['./editor-interface.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class EditorInterfaceComponent
  extends TakeUntilComponentDestroy
  implements OnInit, OnDestroy
{
  private readonly bookACallUrl = 'https://elements.cloud/book-a-call/?step=1';
  // AIChat will be enabled later
  public isShowAIChatButton = false;
  public isShowLegacyNavActions = false;
  public codeSearchButtonTooltip: string;
  public syncInProgress = signal<boolean | null>(false);
  public showCodeSearchButton = signal<boolean>(false);
  anchorTarget: string;
  isStripped: boolean;
  nodesPerPageDefault: number = constants.pagination.nodesPerPageDefault;
  pageSizeOptions: number[] = constants.pagination.pageSizeOptions;
  // todo: update interface with new properties
  user: User;
  userInTeam: UserTeam;
  orgInfo: OrgInfo;
  refModel: Refmodel;
  toolbarConfig: ToolbarConfig;
  isMakingNodeRoot: boolean;
  isRefModel: boolean;
  /** small data from tree */
  selectedRow: TreeNode;
  /** all node data for sidebar */
  selectedNode: any;
  showMaxNodesDOMWarn: boolean;
  showDeleteNodesButton: boolean;

  orgInfoSubscription: Subscription;
  queryParamsSubscription: Subscription;
  rootDataSubscription: Subscription;
  deleteRefModelNodesSubscription: Subscription;

  rootNodesLength$: Observable<number>;
  pageIndex$: Observable<number>;

  orgInfo$: Observable<OrgInfo> = this.rmeStore.select<OrgInfo>(REF_MODEL_STORE.ORG_INFO);
  rootData$: Observable<SalesForceData> = this.rmeStore.select<SalesForceData>(
    REF_MODEL_STORE.ROOT_DATA
  );
  public sidebarOpened$ = new BehaviorSubject<boolean>(true);
  selectedNode$: Observable<RefModelNode> = this.rmeStore.select<RefModelNode>(
    REF_MODEL_STORE.SELECTED_NODE
  );
  refModel$: Observable<Refmodel> = this.rmeStore.select(REF_MODEL_STORE.REF_MODEL);

  isDpActive$: Observable<boolean> = this.rmeStore
    .select(REF_MODEL_STORE.CURRENT_SF_PROCESS)
    .pipe(map((sfProcess: {type: string}) => sfProcess?.type === 'dp-mp'));

  rootNodesLoading$: Observable<any> = this.treePaginationService.selectRootNodesLoading();
  searchNodeConfig: SearchNodeConfig;
  isObjectType: boolean;
  automationTypesList: string[] = [];
  refModelNode: string;
  isProPlusTeam: boolean;
  readonly isFree = this.permissionsService.isFree;
  readonly isMsSpace = this.permissionsService.isMsSpace;
  readonly isTeamAdmin = this.permissionsService.isTeamAdmin;

  hasSubscription(): boolean {
    const user = this.user;

    return !!get(user, 'userInTeam.team.subscription.subscriptionId');
  }

  isExternalRefModel: boolean;
  allowToDownloadCustomViewCsv: boolean;

  hasManageOrEditPermissionsInOrg: boolean;

  isPlayground: boolean = this.permissionsService.isPlayground();
  public showShareRefModelButton = this.rmeStore.canShareRefModel;

  hasAccessToGPTFeatures: boolean = this.permissionsService.hasAccessToGPTFeatures();
  public hasAIAnalysisLicense = this.licenseService.hasOrgAIAnalysisLicense;
  public hasChangeMonitoringLicense = this.licenseService.hasChangeMonitoringLicense;

  public enterpriseGPTEnabled = false;
  public TreeListViewEnum = TreeListViewEnum;

  private dataTypeModel$ = new BehaviorSubject<TreeListViewEnum | null>(TreeListViewEnum.ALL);

  public get dataTypeModel() {
    return this.dataTypeModel$.getValue();
  }

  public set dataTypeModel(value: TreeListViewEnum) {
    this.dataTypeModel$.next(value);
  }

  public selectedSfType = '';
  public sfTypes: {
    name: string;
    value: string;
  }[];
  public customMetadataView$ = new BehaviorSubject<ICustomView | null>(null);
  private selectedKeys$ = this.customGridSelector.selectedKeys$.pipe(
    map(keys => {
      if (this.dataTypeModel === TreeListViewEnum.ALL) {
        return true;
      } else if (keys.length === 1) {
        return true;
      }

      return false;
    })
  );
  public showSidebar$: Observable<boolean> = combineLatest([
    this.rootData$,
    this.selectedNode$,
    this.dataTypeModel$,
    this.selectedKeys$
  ]).pipe(
    map(([rootData, selectedNode, dataTypeModel, selectedKeys]) => {
      const isMetadataDictionary = dataTypeModel === this.TreeListViewEnum.ALL;
      const selectedNodeIsRoot = selectedNode._id === rootData?._id;

      return selectedKeys && (isMetadataDictionary || !selectedNodeIsRoot);
    })
  );

  automationTypes: string[] = [
    'Apex Class',
    'Apex Trigger',
    'Flow',
    'Process Builder Workflow',
    'Validation Rule',
    'Workflow Rule'
  ];

  public enterpriseAIEnabled = false;

  @ViewChild('treeGridWrapper') treeGridWrapper: NodeTreeGridComponent;

  public refModelData: any;

  constructor(
    private rmeStore: RefModelStoreService,
    private route: ActivatedRoute,
    private router: Router,
    public authService: AuthService,
    private refModelService: RefModelService,
    private nodeService: RmeNodeDaoAbstractService,
    public snackBarService: SnackBarService,
    private deleteNodeDialogService: DeleteNodeBaseDialogService,
    private treePaginationService: TreePaginationService,
    private requirementAttachmentsService: RequirementAttachmentsService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private sentryService: SentryService,
    private sfService: SfApiService,
    private attachmentsService: AttachmentsService,
    private updateTreeService: UpdateTreeService,
    private q9HtmlDecoderPipe: Q9HtmlDecoderPipe,
    private fireBaseService: FirebaseAbstractService,
    private rmeNodeDaoService: RmeNodeDaoService,
    private permissionsService: PermissionsService,
    private contextMenuActionsService: ContextMenuActionsService,
    private nodeRemoveEmitterService: NodeRemoveEmitterService,
    private customViewDaoService: CustomViewDaoService,
    private customViewService: CustomViewService,
    private elementsGptService: ElementsGptService,
    private elementsGptExternalService: Q9ElementsGptService,
    private customGridSelector: GridSelectorService,
    private reportDialogService: ReportDialogService,
    public licenseService: LicenseService,
    private intercomEventService: IntercomEventService,
    private actions: Actions,
    private shareRefModelDialogService: ShareRefModelDialogService
  ) {
    super();
  }

  public dataTypeChange($event: {data: string; type?: string; changeUrl?: boolean}): void {
    this.rmeStore.set(REF_MODEL_STORE.SELECTED_NODE, this.refModel);
    this.selectedSfType = '';
    if (isTreeListView($event.data)) {
      const {node} = this.route.snapshot.queryParams;
      const queryParams = {};
      if (node && $event.data === TreeListViewEnum.ALL) {
        queryParams['node'] = node;
      }
      this.router.navigate([`/refmodel/${this.refModel._id}`], {queryParams});
      this.dataTypeModel = $event.data;
      this.customViewService.selectedView = $event.data;
    } else if ($event.type !== 'qc') {
      this.customViewDaoService.getCustomView(this.refModel._id, $event.data).subscribe(
        (customView: ICustomView) => {
          this.allowToDownloadCustomViewCsv = !customView.system;
          const url = `refmodel/${this.refModel._id}/customView/${customView._id}`;
          if ($event.changeUrl) {
            this.router.navigate([url]).then(() => {
              this._updateCustomView(customView as ICustomView);
            });
          } else {
            this._updateCustomView(customView as ICustomView);
          }
        },
        err => {
          this.snackBarService.error(err.error.error);
          this.dataTypeChange({data: TreeListViewEnum.ALL, type: '', changeUrl: true});
        }
      );
    } else {
      this.intercomEventService.sendIntercomEvent(
        this.refModel,
        IntercomEventsByArea.SALESFORCE_METADATA.QUICK_CLEANUP_VIEW_OPENED
      );
      this.customViewDaoService.getQuickCleanUpView(this.refModel._id, $event.data).subscribe(
        (qcView: any) => {
          this.allowToDownloadCustomViewCsv = !qcView.system;
          const url = `refmodel/${this.refModel._id}/qc/${qcView._id}`;
          if ($event.changeUrl) {
            this.router.navigate([url]).then(() => {
              this._updateCustomView(qcView as any);
            });
          } else {
            this._updateCustomView(qcView as any);
          }
        },
        err => {
          this.snackBarService.error(err.error.error);
          this.dataTypeChange({data: TreeListViewEnum.ALL, type: '', changeUrl: true});
        }
      );
    }

    this.dataTypeModel$.next(this.dataTypeModel);
  }

  _updateCustomView(data: ICustomView) {
    this.dataTypeModel = this.TreeListViewEnum.CUSTOM_METADATA;
    this.customMetadataView$.next(data);
    this.customViewService.selectedView = data;
  }

  ngOnInit() {
    this.isProPlusTeam = this.permissionsService.isProPlus();
    this.anchorTarget = this.rmeStore.anchorTarget;
    this.isStripped = this.rmeStore.isStripped;
    this.user = this.rmeStore.user;
    this.hasManageOrEditPermissionsInOrg = this.user.role === 'manage' || this.user.role === 'edit';
    this.userInTeam = this.rmeStore.user.userInTeam;
    this.searchNodeConfig = {
      refModel$: this.refModel$,
      showOverlay: true
    };

    this.refModelService.getOptimizeStatuses().subscribe();

    this.orgInfoSubscription = this.orgInfo$.subscribe((orgInfoData: OrgInfo) => {
      if (orgInfoData) {
        this.orgInfo = {...orgInfoData};
      }
    });

    this.initNodeSubscriptions();
    this.rootNodesLength$ = this.treePaginationService.selectRootNodesLength();
    this.pageIndex$ = this.treePaginationService.selectPageIndex();
    this.sfTypes = formatSfTypes(this.refModel as Refmodel);

    this.requirementAttachmentsService.cleanupRequirementLink(this.user._id);
    this.toolbarConfig = this.getToolbarConfig(this.refModel);
    this.handleRootChange();

    this.rootDataSubscription = this.rootData$.subscribe((data: any) => {
      if (data) {
        const {parentPath} = data;
        this.isObjectType =
          endsWith(parentPath, '.CustomObject') || endsWith(parentPath, '.StandardObject');
        this.refModelNode = data._id;

        if (!this.selectedNode) {
          this.selectedNode = data;
        }
      }

      if (data && !data.nodes?.length) {
        this.treePaginationService.setRootNodesLength(0);
      }

      this.initEnterpriseGPTPermission();
    });

    if (this.isExternalRefModel) {
      this.formAutomationTypesList();
    }

    const customViewId = this.route.snapshot.children[0]?.params.customViewId;
    const qcViewId = this.route.snapshot.children[0]?.params.qcViewId;

    if (customViewId) {
      this.dataTypeChange({data: customViewId});
    } else if (qcViewId) {
      this.dataTypeChange({data: qcViewId, type: 'qc'});
    } else {
      this.dataTypeChange({data: TreeListViewEnum.ALL});
    }

    this.initMakeNodeRootEventSubscription();
    // disabled because of refactoring of context menu
    // this.initRemoveNodeEventSubscription();
    if (this.route.snapshot.queryParams['code-search']) {
      this.contextMenuActionsService.openCodeSearchDialog(this.refModel);
    }

    this.checkChangeMonitoringPaywallRedirect();

    if (this.isExternalRefModel && this.isProPlusTeam) {
      this.fetchCustomViews();
    }

    this.initEnterprisePermission();
  }

  initNodeSubscriptions() {
    const selectedNode$ = this.selectedNode$.pipe(
      this.takeUntilComponentDestroy(),
      tap(node => {
        if (node) {
          this.sidebarOpened$.next(true);
        }
        this.selectedNode = node;
      })
    );
    const refModel$ = this.refModel$.pipe(
      this.takeUntilComponentDestroy(),
      tap(({name, description, ...refModel}: any) => {
        this.refModel = {
          ...refModel,
          name: this.q9HtmlDecoderPipe.transform(name),
          description: this.q9HtmlDecoderPipe.transform(description)
        } as Refmodel;
        this.refModelData = this.refModel;
        this.isExternalRefModel = refModel.external;

        this.syncInProgress.set(refModel.syncStatus === 'inProgress');
        this.showCodeSearchButton.set(
          this.isExternalRefModel &&
            this.user.userInTeam.team.plan.proPlus &&
            this.rmeStore.isEditor
        );

        this.codeSearchButtonTooltip = this._getCodeSearchTooltip();
        if (refModel.external) {
          this.sfService.getSFOrgInfoDataByRefmodel(this.refModel._id).subscribe();
        }
      })
    );

    combineLatest([selectedNode$, refModel$])
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(([node, refModel]) => {
        const isManager = this.user.role === 'manage';
        this.isRefModel = (node || {})._id === refModel._id;

        this.showDeleteNodesButton = isManager && this.isRefModel && refModel.deletedNodesCount > 0;
      });
  }

  initEnterprisePermission() {
    this.elementsGptService
      .getEnterprisePermission()
      .subscribe((data: ExternalGptConfig) => (this.enterpriseAIEnabled = !data.enabled));
  }

  // disabled because of refactoring of context menu
  //   initRemoveNodeEventSubscription() {
  //     this.contextMenuActionsService.removeNodeEmit$
  //       .pipe(
  //         this.takeUntilComponentDestroy(),
  //         filter(node => !!Object.keys(node).length)
  //       )
  //       .subscribe(node => this.removeNode(node));
  //   }

  initMakeNodeRootEventSubscription() {
    this.contextMenuActionsService.makeNodeRootEmit$
      .pipe(
        this.takeUntilComponentDestroy(),
        filter(node => !!Object.keys(node).length)
      )
      .subscribe(node => this.makeNodeRoot(node));
  }

  ngOnDestroy() {
    this.orgInfoSubscription.unsubscribe();
    this.rootDataSubscription.unsubscribe();
    this.queryParamsSubscription.unsubscribe();
    this.deleteRefModelNodesSubscription.unsubscribe();
  }

  handleRootChange(): void {
    this.checkRefModelNodesStatus();
    this.subscribeToDeleteRefModelNodes();
    // TODO Rewrite it in Observable way
    this.queryParamsSubscription = this.route.queryParams.subscribe((params: any) => {
      this.refModelService.fetchNodePath(this.refModel._id, params.node).subscribe();

      if (this.isMakingNodeRoot) {
        this.isMakingNodeRoot = false;
        this.rmeStore.set<SalesForceData>(REF_MODEL_STORE.ROOT_DATA, this.selectedNode);
        const selectedNodeAttachments = this.rmeStore.selectedNodeAttachments;
        if (
          this.selectedNode._id !== this.refModel._id &&
          !selectedNodeAttachments?.loading &&
          this.selectedNode._id === selectedNodeAttachments?.nodeId
        ) {
          this.updateTreeService.set('updateNodeInTree', {
            value: this.attachmentsService.getLinksNumber(selectedNodeAttachments),
            property: 'links',
            nodeId: this.selectedNode._id
          });
        }
      } else if (!params.node) {
        this.rmeStore.set(REF_MODEL_STORE.SELECTED_NODE, this.refModel);
        this.rmeStore.set<SalesForceData>(REF_MODEL_STORE.ROOT_DATA, this.selectedNode);
      } else {
        this.nodeService.getNode(this.refModel._id, params.node).subscribe(data => {
          this.rmeStore.set(REF_MODEL_STORE.SELECTED_NODE, data);
          this.rmeStore.set<SalesForceData>(REF_MODEL_STORE.ROOT_DATA, this.selectedNode);
        });
      }
    });
  }

  getToolbarConfig(refModelMeta: any): ToolbarConfig {
    const index = this.user.userInTeam.teamIndex || 0;
    const title = refModelMeta.external
      ? 'GENERAL.EXTERNAL_REFMODEL_TITLE'
      : 'GENERAL.INTERNAL_REFMODEL_TITLE';
    const refModelPath = refModelMeta.external ? '/referencemodels-external' : '/referencemodels';

    return {
      spaceName: this.userInTeam.team.name,
      domain: this.userInTeam.team.domain,
      homeurl: environment.MAIN_APP_URL + '/t/' + index + refModelPath,
      mainName: this.translate.instant(title),
      topRowText: refModelMeta.name,
      topSubRowText: refModelMeta.description,
      anchorTarget: this.anchorTarget,
      hideLower: false,
      hideSpace: true,
      hideLogoSection: true,
      hideTopRow: this.isStripped,
      showAdvisoryGroupBtn: this.user.advisoryGroupMember
    };
  }

  selectRowHandler(event: any) {
    this.selectedRow = event;
    if (this.refModel._id === event.id) {
      this.rmeStore.set(REF_MODEL_STORE.SELECTED_NODE, this.refModel);
    } else {
      if (this.selectedNode._id === event.id) {
        return;
      }
      this.nodeService.getNode(this.refModel._id, event.id).subscribe(data => {
        this.rmeStore.set(REF_MODEL_STORE.SELECTED_NODE, data);
      });
    }
  }

  removeNode(data: any): void {
    const dialogData = Object.assign({}, data, {refModelId: this.refModel._id});
    const dialogRef = this.deleteNodeDialogService.getDeleteNodeDialog(dialogData);

    dialogRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => {
        const rootData: any = Object.assign({}, this.rmeStore.rootData);
        if (rootData._id === data.nodeId) {
          this.router.navigate(['/refmodel', this.refModel._id]);
        } else {
          this.nodeRemoveEmitterService.emitNodeRemoved(data);
        }
      });
  }

  removeMultipleNodes(data: any = {multiple: true}): void {
    const dialogParams = Object.assign({}, data, {
      refModelId: this.refModel._id
    });

    const dialogRef = this.dialog.open(DeleteNodeDialogComponent, {
      width: constants.dialog.width.md,
      panelClass: ['q9-dialog', 'q9-dialog-danger'],
      data: dialogParams
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.rmeStore.set(REF_MODEL_STORE.ARE_NODES_DELETING, true);
        this.snackBarService.info('NOTIFICATIONS.DELETE_NODES_STARTED');
      }
    });
  }

  subscribeToDeleteRefModelNodes(): void {
    this.deleteRefModelNodesSubscription = this.fireBaseService
      .onChildRemove(`referenceModels/${this.refModel._id}`)
      .subscribe((data: SnapshotAction<{status: DeleteReferenceModelNodesStatusEnum}>) => {
        if (data?.key === 'refModelNodesAreDeleting') {
          const payload = data?.payload?.val();
          this.refModelService
            .getRefModelMeta(this.refModel._id)
            .subscribe((refModel: SalesForceData) => {
              this.rmeStore.set(REF_MODEL_STORE.ARE_NODES_DELETING, false);
              this.rmeStore.set(REF_MODEL_STORE.REF_MODEL, refModel);

              if (payload?.status === DeleteReferenceModelNodesStatusEnum.COMPLETED) {
                this.snackBarService.success('NOTIFICATIONS.DELETE_NODES_FINISHED');
              } else {
                this.snackBarService.error('NOTIFICATIONS.DELETE_NODES_FAILED');
              }
            });
        }
      });
  }

  checkRefModelNodesStatus(): void {
    this.rmeStore.set(REF_MODEL_STORE.ARE_NODES_DELETING, false);
    this.fireBaseService
      .getReference(`referenceModels/${this.refModel._id}`)
      .on('value', snapshot => {
        const data = snapshot.val();

        if (
          data?.refModelNodesAreDeleting?.status === DeleteReferenceModelNodesStatusEnum.IN_PROGRESS
        ) {
          this.rmeStore.set(REF_MODEL_STORE.ARE_NODES_DELETING, true);
        }
      });
  }

  /** Pagination */
  paginationChange(event: any): void {
    this.treePaginationService.setNodesPerPage(event.pageSize);
    this.treePaginationService.setPageIndex(event.pageIndex);
    this.treePaginationService.setRootNodesLoading(true);
    this.refreshTree();
  }

  showMaxNodesDOMWarnCallback(event: {show: boolean}) {
    this.showMaxNodesDOMWarn = event.show;
  }

  /** Toolbar */
  // TODO Create separate component for toolbar
  goHome(): void {
    const index = this.user.userInTeam.teamIndex || 0;
    const refModelPath = this.refModel.external ? 'referencemodels-external' : 'referencemodels';
    const url = `${environment.MAIN_APP_URL}/t/${index}/${refModelPath}`;
    window.open(url, this.anchorTarget);
  }

  collapseTree(): void {
    this.sentryService.trackUserAction('Collapse tree');

    this.treeGridWrapper.collapseAllNodes();
  }

  refreshTree(): void {
    const rootId = this.rmeStore.rootData._id;
    const rootIsRefModel = rootId === this.refModel._id;

    this.sentryService.trackUserAction('Refresh tree', {rootId, rootIsRefModel});
    /** Take needed api call based on type of root node */

    const selectedNode$ = rootIsRefModel
      ? this.refModelService.fetchRefModelMeta(this.refModel._id)
      : this.nodeService.getNode(this.refModel._id, rootId);

    const obsevablesArr = [selectedNode$];
    if (this.refModel.external) {
      obsevablesArr.push(this.sfService.getSFOrgInfoDataByRefmodel(this.refModel._id));

      if (this.isProPlusTeam) {
        obsevablesArr.push(this.customViewDaoService.getCustomViews(this.refModel._id));
      } else {
        obsevablesArr.push(of([]));
      }

      obsevablesArr.push(this.refModelService.getOptimizeStatuses());

      if (this.customViewService.selectedView && this.customViewService.selectedView['_id']) {
        obsevablesArr.push(
          of(this.dataTypeChange({data: this.customViewService.selectedView['_id']}))
        );
      }
    }
    combineLatest(obsevablesArr).subscribe(([selectedNode, orgInfo, customViews]: any[]) => {
      this.rmeStore.set(REF_MODEL_STORE.SELECTED_NODE, selectedNode);
      this.rmeStore.set<SalesForceData>(REF_MODEL_STORE.ROOT_DATA, this.selectedNode);
      this.rmeStore.set<OrgInfo>(REF_MODEL_STORE.ORG_INFO, orgInfo);
      this.customViewService.storeCustomViewList(customViews);
      if (!rootIsRefModel) {
        this.attachmentsService.fetchAttachments(this.refModel._id, rootId).subscribe();
      }
      if (this.dataTypeModel !== TreeListViewEnum.ALL) {
        this.customGridSelector.resetSelectedKeys();
      }
    });

    this.actions.dispatch(
      loadEntities({reset: this.dataTypeModel !== TreeListViewEnum.MY_METADATA})
    );
  }

  makeNodeRoot(selectedRow: {id: string}): void {
    if (!selectedRow) {
      return;
    }

    this.sentryService.trackUserAction('Make node root', {nodeId: selectedRow.id});

    const navigationExtras: NavigationExtras = {
      queryParams: {node: selectedRow.id}
    };

    if (this.isStripped) {
      navigationExtras['queryParams']['stripped'] = true;
    }

    this.treePaginationService.setPageIndex(0);

    this.isMakingNodeRoot = true;
    this.router.navigate(['/refmodel', this.refModel._id], navigationExtras).then(() => {
      this.showMaxNodesDOMWarn = false;
    });
  }

  goToSfAnalytics(): void {
    window.open(
      '/architecture-explorer/' + this.rmeStore.refModel._id + '/org-glance',
      this.anchorTarget
    );
  }

  goToAccessChangeMonitoring(): void {
    this.intercomEventService.sendIntercomEvent(
      this.refModel,
      IntercomEventsByArea.CHANGE_MONITORING.CLICKED,
      IntercomProductAreas.CHANGE_MONITORING
    );
    if (!this.licenseService.hasChangeMonitoringLicense()) {
      this.dialog.open(ChangeMonitoringPaywallComponent, {
        width: DialogWidth.LG_XL,
        disableClose: false,
        panelClass: 'paywall-dialog'
      });
      return;
    }
    window.open(`/refmodel/${this.refModel._id}/monitoring`, '_blank', 'noopener,noreferrer');
  }

  public openPricingCalculator(): void {
    if (this.isMsSpace()) {
      this.dialog.open(ElementsPricingCalculatorComponent, {
        width: DialogWidth.LG_XL
      });
      return;
    }

    window.open(this.bookACallUrl, '_blank');
  }

  openConversation() {
    this.dialog.open(AiChatDialogComponent, {
      width: constants.dialog.width.xlg,
      panelClass: 'q9-dialog'
    });
  }

  downloadCustomViewCsv(): void {
    const customView = this.customMetadataView$.getValue();

    this.customViewDaoService.downloadCustomViewCsv(this.refModel._id, customView._id).subscribe(
      response => {
        const message = this.translate.instant('RUBIC_CUBE.DOWNLOADING_STARTED', {
          name: customView.name
        });

        this.snackBarService.success(message);
      },
      errorResponse => {
        const message = this.translate.instant('RUBIC_CUBE.START_DOWNLOADING_FAILURE', {
          name: customView.name
        });

        const {error} = errorResponse.error;

        this.snackBarService.error(error || message);
      }
    );
  }

  public generateBigDataReport(type: string, automationType?: string) {
    const params: BDReportParams = {
      refModelId: this.refModel._id,
      type
    };

    if (type === 'refModelFieldDependencies') {
      params['refModelNode'] = !this.isRefModel && this.refModelNode;
    } else {
      params['automationType'] = automationType;
    }

    this.rmeNodeDaoService.generateBigDataReport(params).subscribe(res => {
      if (type === 'refModelPermissionSimilarityScore' && res?.reason) {
        this.snackBarService.info(res.reason);
      } else if (res.inProgress) {
        this.snackBarService.info('NOTIFICATIONS.GEN_REPORT');
      }
    });
  }

  public onSfTypeSelectionChange(event): void {
    this.selectedSfType = event.value;
  }

  private checkChangeMonitoringPaywallRedirect(): void {
    if (this.route.snapshot.queryParams['paywallFor'] !== 'changeMonitoring') return;

    this.dialog.open(ChangeMonitoringPaywallComponent, {
      width: DialogWidth.LG_XL,
      disableClose: false,
      panelClass: 'paywall-dialog'
    });
    this.router.navigate([], {
      queryParams: {paywallFor: null},
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private initEnterpriseGPTPermission(): void {
    this.elementsGptExternalService.getExternalGptConfig().subscribe(config => {
      this.enterpriseGPTEnabled = !config?.enabled;
    });
  }

  private formAutomationTypesList(): void {
    const sfTypes = [...this.refModel.sfTypes, ...constants.additionalSearchFilterSfTypes];
    for (const type of this.automationTypes) {
      if (sfTypes.includes(type)) {
        this.automationTypesList.push(type);
      }
    }
  }

  private fetchCustomViews() {
    this.customViewDaoService.getCustomViews(this.refModel._id).subscribe(customViews => {
      this.customViewService.storeCustomViewList(customViews);
    });
  }

  public handleDelete(deletedNode) {
    if (deletedNode) {
      this.nodeRemoveEmitterService.emitNodeRemoved({
        nodeId: deletedNode._id
      } as ITreeNode);
    }
  }

  public onMpDpStarted() {
    this.rmeStore.set(REF_MODEL_STORE.CURRENT_SF_PROCESS, {type: 'dp-mp'});
  }

  public showSavedReports(modalType: REPORT_MODAL_TYPE): void {
    const currentView = this.customViewService.selectedView;

    this.reportDialogService.openReportDialog(
      modalType,
      this.rmeStore.selectedNode,
      this.refModel,
      this.rmeStore.rootData,
      currentView
    );
  }

  public addProposedNode(item) {
    this.contextMenuActionsService.openProposedNodeCreatingDialog(item, item.childTypes[0]);
  }

  public openCodeSearchModal() {
    this.intercomEventService.sendIntercomEvent(
      this.refModel,
      IntercomEventsByArea.SALESFORCE_METADATA.FIND_METADATA_REFERENCES_CLICKED
    );

    this.dialog
      .open(RunToolbarCodeSearchDialogComponent, {
        width: constants.dialog.width.lg,
        disableClose: true,
        panelClass: ['q9-dialog']
      })
      .afterClosed()
      .subscribe();
  }

  private _getCodeSearchTooltip(): string {
    if (this.syncInProgress()) {
      return this.translate.instant('CONTEXT_MENU.RUN_CODE_SEARCH_DISABLED_TOOLTIP');
    }

    return this.translate.instant('CORE.SIDEBAR.FIND_METADATA_REFERENCES_TOOLTIP');
  }

  public onShareRefModel() {
    this.shareRefModelDialogService.openRefModelShareDialog();
  }
}
